/**
 * Общие константы и утилиты для модальных окон блоков дашборда.
 * 
 * Используются в AddBlockModal, EditBlockModal.
 */

import React from 'react';
import {
  PieChartOutlined,
  TableOutlined,
  FieldBinaryOutlined,
  FontSizeOutlined,
} from '@ant-design/icons';

import FirstRegIcon from '../../../../shared/common/components/reports/params/attribution/icons/FirstRegIcon';
import LastRegIcon from '../../../../shared/common/components/reports/params/attribution/icons/LastRegIcon';
import TypeIcon from '../../../../shared/common/components/reports/params/attribution/icons/TypeIcon';
import { dateObjects } from '../../helpers/data';

// ==================== ТИПЫ ====================

export type BlockDisplayType = 'chart' | 'table' | 'number' | 'heading';
export type BlockDateType = 'reg' | 'ad' | 'order' | 'payment' | 'web' | 'visit';

export interface HeadingStyles {
  fontSize: number;
  fontWeight: number;
  color: string;
}

// ==================== КОНСТАНТЫ ====================

/**
 * Элементы атрибуции для настроек блока.
 */
export const attrItems = [
  {
    id: 'type_applications',
    name: 'Тип регистрации:',
    selectValues: [
      { name: 'Нулевые заказы GC', id: '2', icon: null, default: true },
      { name: 'Прямая продажа', id: '4', icon: null },
      { name: 'Регистрации GC', id: '0', icon: null },
      { name: 'SCAN', id: '1', icon: null },
    ],
    description: `Выбор типа регистрации по которым нужно создать отчёт:
                    <br><br>
                    Типы регистраци:
                    <br><br>
                    - SCAN - Это данные собираемые скриптом SCAN с форм;<br>
                    - Прямая продажа - эта регистрация создаётся автоматически в SCANе при создании платного заказа. Название регистрации будет являться название платного предложения;<br>
                    - Нулевые заказы GC - Заказы в GetCourse со стоимостью 0 руб.;<br>
                    - Регистрации GC -  Первая регистрация (попадания в базу) в GetCourse;<br>
                    <br><br>
                    <a href="https://doc.scananalytics.ru/otchety/tip-registracii/" rel="noopener noreferrer" target="_blank">Подробнее</a>.
                    `,
    icon: <TypeIcon />,
  },
  {
    id: 'attribution',
    name: 'Атрибуция:',
    selectValues: [
      { name: 'Первая регистрация', id: '1', icon: <FirstRegIcon /> },
      { name: 'Последняя регистрация', id: '2', icon: <LastRegIcon /> },
    ],
    description: `Сервис SCAN позволяет определить к какой регистрации пользователя привязывать заказ. В сервисе 2 типа атрибуции:
                    <br><br>
                    - Первая регистрация <br>
                    - Последняя регистрация <br>
                    <br><br>
                    <a href="https://doc.scananalytics.ru/otchety/atribuciya/" rel="noopener noreferrer" target="_blank">Подробнее</a>.
                    `,
    icon: null,
  },
];

/**
 * Элементы выбора периода.
 */
export const displayItems = [
  {
    id: 'date',
    name: 'Период:',
    selectValues: dateObjects,
    description: null,
    icon: null,
  },
];

/**
 * Кнопки выбора типа отображения блока.
 */
export const displayTypeButtons = [
  {
    id: 'chart',
    name: 'Диаграмма',
    icon: <PieChartOutlined />,
  },
  {
    id: 'table',
    name: 'Таблица',
    icon: <TableOutlined />,
  },
  {
    id: 'number',
    name: 'Число',
    icon: <FieldBinaryOutlined />,
  },
  {
    id: 'heading',
    name: 'Заголовок',
    icon: <FontSizeOutlined />,
  },
];

/**
 * Кнопки выбора типа отображения (без heading).
 */
export const displayTypeButtonsWithoutHeading = displayTypeButtons.filter(
  (btn) => btn.id !== 'heading'
);

/**
 * Дефолтные стили для heading блока.
 */
export const defaultHeadingStyles: HeadingStyles = {
  fontSize: 20,
  fontWeight: 800,
  color: '#111827',
};

// ==================== УТИЛИТЫ ====================

/**
 * Определяет тип отчёта из даты блока дашборда.
 * @param date - Дата в формате "type" или "type_start_end"
 * @returns Тип отчёта для ReportChart
 */
export const getReportTypeFromDate = (
  date?: string
): 'registration' | 'order' | 'payment' | 'ad' | 'webinar' | 'visit' => {
  if (!date) return 'registration';

  const parts = date.split('_');
  const type = parts[0];

  switch (type) {
    case 'reg':
    case 'registration':
    case 'universal':
      return 'registration';
    case 'ad':
      return 'ad';
    case 'order':
      return 'order';
    case 'payment':
      return 'payment';
    case 'web':
    case 'webinar':
      return 'webinar';
    case 'visit':
      return 'visit';
    default:
      return 'registration';
  }
};

/**
 * Преобразует тип отчёта из ReportChart в ReportTable.
 */
export const chartToTableReportType = (
  chartType: 'registration' | 'order' | 'payment' | 'ad' | 'webinar' | 'visit'
): 'universal' | 'order' | 'payment' | 'ad' | 'webinar' | 'visit' => {
  if (chartType === 'registration') return 'universal';
  return chartType;
};

/**
 * Получить список фильтров в зависимости от типа периода.
 */
export const getFiltersFilterList = (dateType?: string): string[] => {
  const baseFilters = [
    'Пользователи',
    'Регистрации',
    'Заявки',
    'Заказы',
    'Платежи',
    'Вебинары',
  ];
  
  if (dateType === 'ad') {
    return [...baseFilters, 'Реклама'];
  }
  
  return baseFilters;
};
